import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';

interface IChangePinRequest {
  oldPin: string;
  newPin: string;
}

interface IChangePinResponse {
  status?: boolean;
  message?: string;
  data?: unknown;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IChangePinResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const changePinAsync = createAsyncThunk(
  'pin/change',
  async (
    values: IChangePinRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IChangePinResponse>(
        `/auth/change-pin`,
        values,
        config
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Pin has been updated successfully',
        })
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IChangePinResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'error',
          description: errorMessage,
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const pinUpdateSlice = createSlice({
  name: 'pin/change',
  initialState,
  reducers: {
    resetPinUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changePinAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(changePinAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IChangePinResponse;
      })
      .addCase(changePinAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IChangePinResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetPinUpdate } = pinUpdateSlice.actions;

export default pinUpdateSlice.reducer;
